<template>
  <div id="LmFormBuilder" class="wrapper-content w-100">
    <LmLoading v-if="isLoading" absolute />
    <template v-show="!isLoading">
      <h1 v-if="pageTitleData && !disablePageTitleData">{{formTitle || pageTitleData}}</h1>
      <LmForm ref="lmFormRef" @onEditMode="onEditMode($event)">
        <portal-target name="destination-after-title" slim/>
        <template v-for="(formRow, index) in formElements" >
          <component
            :id="`${formRow.componentName}-${index}`"
            v-if="formRow.componentName !== 'LmButton' && isVisibleOnlyOnEditMode(formRow) && isVisible(formRow)"
            :ref="`componentRef${index}`"
            :disabled="isDisabledOnlyOnEditMode(formRow) || componentIsDisabled[formRow.formObjectToSendToServerProperty] || formRow.componentProps.disabled || isDisabled || isDisabledByEditor(formRow) || isDisabledInDocumentCtx(formRow)"
            :key="index"
            :class="formRow.class"
            :is="formRow.componentName"
            :v="$v.formObjectToSendToServer[formRow.formObjectToSendToServerProperty]"
            :showEditButton="isOnPreviewMode"
            :visibility="formRow.visibilityDependOnConditions"
            v-bind="formRow.componentProps"
            :modelName="formRow.formObjectToSendToServerProperty"
            v-model="formObjectToSendToServer[formRow.formObjectToSendToServerProperty]"
            :defaultValue="formRow.componentName === 'LmSuperPowerSelectV2' || formRow.componentName === 'LmTreeSelect' ? defaultValueOnSuperPower(formRow) : null"
            @edit-button-clicked="click(formRow, index)"
            @onUpdate="formObjectToSendToServer[formRow.formObjectToSendToServerProperty] = $event"
            @filesToRemoveFromServer="filesToRemove = $event"
            @uploadProcessing="uploadProcessing = $event"
            @mentions-list="$emit('mentions-list', $event)"
            @editor-change="onEditorChange(formRow, $event)"
            @editor-change-on-subelement="onEditorChangeOnSubElement(formRow, $event)"
            @regenerate-validations="onRegenerateValidations"
            :editor="formRowEditor(formRow)"
            :editors="formRow.componentName === 'LmRepeatFields' ? editors : null"
          />
          <portal-target v-if="shouldCreatePortal(formRow.componentName)" :key="`destination-key-${index}`" :name="`destination-${index}`" slim/>
          <component
            v-if="conditionsForRenderComponent(formRow)"
            :disabled="conditionsForDisableComponent(formRow)"
            :key="index"
            :class="formRow.class"
            :is="formRow.componentName"
            v-bind="formRow.componentProps"
            :id="formRow.id"
            :action="formRow.actions.click.type"
            :visibility="formRow.visibilityDependOnConditions"
            @click="click(formRow)"
          />
        </template>
        <portal-target name="button-submit" slim/>
      </LmForm>
      <h4 v-if="emptyFormFields" class="text-center">No hay campos para mostrar</h4>
    </template>
  </div>
</template>

<script>
import {
  required,
  email,
  minLength,
  maxLength,
  numeric,
  sameAs
} from 'vuelidate/lib/validators'

import commonsApi from '@/api/commons'
import _ from 'lodash'

// Components
import LmForm from '@/components/form/LmForm'
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmPassword from '@/components/form/elements/LmPassword'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmNumberInput from '@/components/form/elements/LmNumberInput'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2'
import LmDate from '@/components/form/elements/LmDate'
import LmDateTime from '@/components/form/elements/LmDateTime'
import LmTextArea from '@/components/form/elements/LmTextArea'
import LmSelect from '@/components/form/elements/LmSelect'
import LmRadio from '@/components/form/elements/LmRadio'
import LmFormSubtitles from '@/components/form/elements/LmFormSubtitles'
import LmFormEmptySpace from '@/components/form/elements/LmFormEmptySpace'
import LmFormSeparator from '@/components/form/elements/LmFormSeparator'
import LmSuperPowerSelectV2 from '@/components/form/elements/LmSuperPowerSelectV2.vue'
import LmEditor from '@/components/form/elements/lmEditor'
import LmLeaFletMap from '@/components/form/elements/LmLeaFletSearcherMap.vue'
import LmHtmlContent from '@/components/form/elements/LmHtmlContent'
import LmPortalTarget from '@/components/form/elements/LmPortalTarget'
import LmColorPicker from '@/components/form/elements/LmColorPicker'
import LmComment from '@/components/LmComment'
import LmRepeatFields from '@/components/form/elements/LmRepeatFields'
import LmCheckbox from '@/components/form/elements/LmCheckbox'
import LmUploadFiles from '@/components/form/elements/LmUploadFiles'
import LmUploadImage from '@/components/form/elements/LmUploadImage'
import LmImportFromExcel from '@/components/form/elements/LmImportFromExcel'
import LmCustom from '@/components/form/elements/LmCustom'
import LmParticipants from '@/components/form/elements/LmParticipants'
import LmCodeInput from '@/components/form/elements/LmCodeInput'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmTagsInput from '@/components/form/elements/LmTagsInput'
import LmReaderInput from '@/components/form/elements/LmReaderInput'

// Utils
import { Spanish } from 'flatpickr/dist/l10n/es.js'
import lmToasts from '@/mixins/lmToasts.js'

export default {
  provide () {
    return {
      context: this.formObjectToSendToServer,
      isOnPreviewMode: this.isOnPreviewMode,
      isOnEditMode: this.isParentOnEditMode
    }
  },
  inject: {
    document: {
      type: Object,
      default: null
    }
  },
  mixins: [lmToasts],
  components: {
    LmForm,
    LmButton,
    LmTextInput,
    LmPassword,
    LmEmailInput,
    LmNumberInput,
    LmTextArea,
    LmSelect,
    LmRadio,
    LmFormSubtitles,
    LmFormEmptySpace,
    LmFormSeparator,
    LmDate,
    LmDateTime,
    LmSuperPowerSelectV2,
    LmEditor,
    LmLeaFletMap,
    LmHtmlContent,
    LmPortalTarget,
    LmColorPicker,
    LmComment,
    LmRepeatFields,
    LmCheckbox,
    LmUploadFiles,
    LmUploadImage,
    LmImportFromExcel,
    LmNumberInputV2,
    LmCustom,
    LmParticipants,
    LmCodeInput,
    LmTreeSelect,
    LmTagsInput,
    LmReaderInput
  },
  computed: {
    emptyFormFields () {
      return !this.formElements.some(el => this.isVisible(el)) && this.compactMode
    }
  },
  data () {
    return {
      isLoading: false,
      componentIsDisabled: {},
      registerOfDataWatchDinamically: {},
      pageTitleData: '',
      messageAfterPOSTorPUT: 'Guardado',
      pathToRedirectAfterPOSTorPUT: '/',
      apiRestForHandleAllHttpMethods: '',
      formObjectToSendToServer: {},
      formElements: [], // el array con todos elementos que componen el formulario
      validationsData: {},
      originalValidations: {},
      conditionalOnChange: [],
      isOnEditMode: false,
      filesToRemove: [],
      uploadProcessing: false,
      firstLoad: true
    }
  },
  validations () {
    const validationsRules = {
      formObjectToSendToServer: {}
    }
    for (const property in this.validationsData) {
      validationsRules.formObjectToSendToServer[property] = {}
      for (const innerObjectProperty in this.validationsData[property]) {
        switch (innerObjectProperty) {
          case 'required':
            if (this.validationsData[property][innerObjectProperty]) {
              validationsRules.formObjectToSendToServer[property][innerObjectProperty] = required
            } else {
              delete validationsRules.formObjectToSendToServer[property][innerObjectProperty]
            }
            break
          case 'email':
            if (this.validationsData[property][innerObjectProperty]) {
              validationsRules.formObjectToSendToServer[property][innerObjectProperty] = email
            } else {
              delete validationsRules.formObjectToSendToServer[property][innerObjectProperty]
            }
            break
          case 'minLength':
            if (this.validationsData[property][innerObjectProperty]) {
              validationsRules.formObjectToSendToServer[property][innerObjectProperty] = minLength(this.validationsData[property][innerObjectProperty])
            } else {
              delete validationsRules.formObjectToSendToServer[property][innerObjectProperty]
            }
            break
          case 'maxLength':
            if (this.validationsData[property][innerObjectProperty]) {
              validationsRules.formObjectToSendToServer[property][innerObjectProperty] = maxLength(this.validationsData[property][innerObjectProperty])
            } else {
              delete validationsRules.formObjectToSendToServer[property][innerObjectProperty]
            }
            break
          case 'numeric':
            validationsRules.formObjectToSendToServer[property][innerObjectProperty] = numeric
            break
          case 'sameAs':
            if (this.validationsData[property][innerObjectProperty]) {
              validationsRules.formObjectToSendToServer[property][innerObjectProperty] = sameAs(this.validationsData[property][innerObjectProperty])
            } else {
              delete validationsRules.formObjectToSendToServer[property][innerObjectProperty]
            }
            break

          default:
            break
        }
      }
    }
    // Validacion de los items dentro de un componente
    // El componente debe tener "validationsItems" como lo hace LmTagsInput
    const validationsItems = { $each: {} }
    for (const element of this.formElements) {
      if (element?.validationsItems) {
        for (const validation in element.validationsItems) {
          if (element.validationsItems[validation]) {
            switch (validation) {
              case 'email':
                validationsItems.$each[validation] = email
                break
              case 'maxLength':
                validationsItems.$each[validation] = maxLength(element.validationsItems[validation])
                break
              case 'minLength':
                validationsItems.$each[validation] = minLength(element.validationsItems[validation])
                break
            }
            if (validationsRules.formObjectToSendToServer.hasOwnProperty(element.formObjectToSendToServerProperty)) {
              Object.assign(validationsRules.formObjectToSendToServer[element.formObjectToSendToServerProperty], validationsItems)
            } else {
              _.set(validationsRules.formObjectToSendToServer, element.formObjectToSendToServerProperty, validationsItems)
            }
          }
        }
      }
    }
    return validationsRules
  },
  props: {
    compactMode: {
      type: Boolean,
      default: false
    },
    isOnPreviewMode: {
      type: Boolean,
      default: false
    },
    getComponents: {
      type: Object,
      default: () => ({})
    },
    endpointUriwhereGetComponents: {
      type: String,
      default: ''
    },
    endpointUriWhereGetValuesToLoadInEditMode: {
      type: String,
      default: ''
    },
    contentTemplateData: {
      type: Object,
      default: () => ({})
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    disablePageTitleData: {
      type: Boolean,
      default: false
    },
    formTitle: {
      type: String,
      default: null
    },
    formValues: {
      type: Object,
      default: null
    },
    editors: {
      type: Array,
      default: () => []
    },
    isParentOnEditMode: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onRegenerateValidations () {
      const validations = _.cloneDeep(this.originalValidations)
      if (validations) {
        for (const ref in this.$refs) {
          if (/^componentRef\d+/.test(ref)) {
            const component = this.$refs[ref][0]
            if (component?.modelName && !component?.showComponent) {
              delete validations[component.modelName]
            }
          }
        }
      }
      this.validationsData = validations
    },
    onEditorChange (formRow, event) {
      if (this.validationsData) {
        const currentUser = this.$store.getters['session/userInfo']
        const validations = _.cloneDeep(this.validationsData)
        if (event.id !== currentUser.id) {
          delete validations[formRow.formObjectToSendToServerProperty]
        } else {
          validations[formRow.formObjectToSendToServerProperty] = this.originalValidations[formRow.formObjectToSendToServerProperty]
        }
        this.validationsData = validations
      }
      this.$emit('editor-change', { editor: event, element: formRow })
    },
    onEditorChangeOnSubElement (formRow, event) {
      this.$emit('editor-change', { editor: event.editor, element: formRow, subelement: event.subelement })
    },
    formRowEditor (formRow) {
      return this.editors.find(el => el.element === formRow.formObjectToSendToServerProperty) || {}
    },
    validate () {
      return new Promise((resolve, reject) => {
        let error = false
        const validations = this.$v
        validations.$touch()
        error = validations.$error
        if (error) {
          for (const key in this.formObjectToSendToServer) {
            if (validations.formObjectToSendToServer.hasOwnProperty(key)) {
              if (validations.formObjectToSendToServer[key].$error) {
                const componentIndex = this.formElements.findIndex(el => el.formObjectToSendToServerProperty === key)
                const component = this.formElements[componentIndex]
                const selector = this.$el.querySelector(`#${component.componentName}-${componentIndex}`)
                selector.scrollIntoView({ behavior: 'smooth' })
                break
              }
            }
          }
        }

        this.formElements.forEach((el, index) => {
          if (el.componentName === 'LmRepeatFields') {
            if (this.$refs[`componentRef${index}`] && !this.isDisabledInDocumentCtx(el)) {
              const repeatableValidations = this.$refs[`componentRef${index}`][0].$v
              repeatableValidations.$touch()
              error = repeatableValidations.$error
              if (error) {
                const selector = this.$el.querySelector(`#${el.componentName}-${index}`)
                selector.scrollIntoView({ behavior: 'smooth' })
              }
            }
          }
        })
        if (error) {
          this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error de validación')
          reject(new Error('validation error'))
        } else {
          resolve(true)
        }
      })
    },
    conditionsForRenderComponent (component) {
      if (
        component.componentName === 'LmButton' &&
        this.isVisibleOnlyOnEditMode(component) &&
        (component.actions.click.type !== 'delete' || (component.actions.click.type === 'delete' && this.isOnEditMode))
      ) {
        return true
      } else {
        return false
      }
    },
    conditionsForDisableComponent (component) {
      if (
        this.isDisabledOnlyOnEditMode(component) ||
        component.componentProps.disabled ||
        this.uploadProcessing
      ) {
        return true
      } else {
        return false
      }
    },
    async removeFilesFromServer () {
      try {
        await commonsApi.axiosPostWrapper(`${process.env.VUE_APP_API_URL}removefiles/`, {
          filesToRemove: this.filesToRemove
        })
      } catch (error) {
        console.log(error)
      }
    },
    defaultValueOnSuperPower (formRow) {
      if (formRow?.childrenOf) {
        return this.formObjectToSendToServer[formRow.formObjectToSendToServerProperty]
      } else {
        return formRow?.componentProps?.defaultValue
      }
    },
    isVisibleOnlyOnEditMode (formRow) {
      if (formRow && 'componentProps' in formRow) {
        if ('isVisibleOnlyOnEditMode' in formRow.componentProps) {
          if ((formRow?.componentProps?.isVisibleOnlyOnEditMode && this.isOnEditMode) || !formRow?.componentProps?.isVisibleOnlyOnEditMode) {
            return true
          }
          return false
        } else {
          return true
        }
      }
    },
    isDisabledOnlyOnEditMode (formRow) {
      if (formRow && 'componentProps' in formRow) {
        if ('isDisabledOnlyOnEditMode' in formRow.componentProps) {
          if (formRow?.componentProps?.isDisabledOnlyOnEditMode && this.isOnEditMode) {
            return true
          }
          return false
        } else {
          return false
        }
      }
    },
    isVisible (formRow) {
      if (
        this.compactMode &&
        (this.isDisabledByEditor(formRow) || this.isDisabledInDocumentCtx(formRow))
      ) {
        return false
      }

      return true
    },
    isDisabledInDocumentCtx (formRow) {
      if (this.document === null) return false

      if (this.document.isStarted) {
        if (!this.editors.some(el => el.element === formRow.formObjectToSendToServerProperty)) {
          return true
        }
      }
      return false
    },
    isDisabledByEditor (formRow) {
      if (this.editors.length) {
        if (formRow.componentName !== 'LmRepeatFields') {
          const editor = this.formRowEditor(formRow)
          if (Object.keys(editor).length) {
            if (this.$store.getters['session/userInfo'].id !== editor?.id) {
              return true
            }
            return false
          }
        }
        if (formRow.componentName === 'LmRepeatFields') {
          return !this.editors.some(el => {
            return (
              el.element === formRow.formObjectToSendToServerProperty &&
              el.id === this.$store.getters['session/userInfo'].id
            )
          })
        }
      }
      return false
    },
    onEditMode (event) {
      this.isOnEditMode = event || false
    },
    shouldCreatePortal (componentName) {
      return componentName !== 'LmComment'
    },
    defineValidationsData (data) {
      const currentUser = this.$store.getters['session/userInfo']
      const validations = data
      if (Object.keys(this.editors).length) {
        for (const key in data) {
          const editor = this.editors.find(editor => editor.element === key)
          if (editor && editor.id !== currentUser.id) {
            delete validations[key]
          }
        }
      }
      return validations
    },
    async initializeComponent () {
      // Si this.getComponents tiene elementos formbuilder está operando en modo edicion
      // del formulario, ej /FormBuilderUi/edit/[id_de_un_formbuilder]
      if (Object.keys(this.getComponents).length) {
        this.formElements = _.cloneDeep(this.getComponents.formElements)
        this.validationsData = _.cloneDeep(this.getComponents.validations)
        this.originalValidations = _.cloneDeep(this.getComponents.validations)
      } else {
        // Formbuilder esta operando en modo visualizacion
        // Obtenemos toda la informacion del formulario desde la API
        const endPoint = await commonsApi.axiosGetWrapper(this.endpointUriwhereGetComponents)
        this.pageTitleData = endPoint.data.pageTitleData
        this.messageAfterPOSTorPUT = endPoint.data.messageAfterPOSTorPUT
        this.pathToRedirectAfterPOSTorPUT = endPoint.data.pathToRedirectAfterPOSTorPUT
        this.apiRestForHandleAllHttpMethods = endPoint.data.apiRestForHandleAllHttpMethods
        this.formElements = endPoint.data.formElements
        this.validationsData = this.defineValidationsData(endPoint.data.validations)
        this.originalValidations = _.cloneDeep(endPoint.data.validations)
      }
      // Los super power hijos no cargo los valores
      for (const el of this.formElements) {
        if (el?.childrenOf && el?.childrenOf !== null) {
          el.componentProps.disabled = true
        }
      }
      this.$emit('formBuilderInitialized')
    },
    getValueFromFormElements (value) {
      return value?.id || value
    },
    /**
     * Obtiene desde el backend los datos asociados al registro que se está editando
     *
     * activeEditMode es parte del mixin lmFormMixinV2 que se carga en el componente LmForm
     *
     * emite formLoadingComplete
     */
    async setForm () {
      if (this.endpointUriWhereGetValuesToLoadInEditMode) {
        await this.$refs.lmFormRef.activeEditMode({
          path: this.endpointUriWhereGetValuesToLoadInEditMode,
          id: '',
          paramsMode: true }, 'formObjectToSendToServer')
        this.$emit('formLoadingComplete')
      }

      if (this.isOnEditMode && this.formValues) {
        for (const key in this.formValues) {
          if (this.formObjectToSendToServer.hasOwnProperty(key)) {
            this.formObjectToSendToServer[key] = this.formValues[key]
          }
        }
      }
    },
    async click (formRow, index) {
      if (this.isOnPreviewMode) {
        this.$emit('openEditFormElement', index)
      }
      try {
        if (formRow?.actions) {
          switch (formRow?.actions?.click?.type) {
            case 'submit':
            // Validación general
              await this.validate()
              // Validación de los campos repetitivos
              const repeatFieldsIndex = this.formElements.findIndex(el => el.componentName === 'LmRepeatFields')
              if (repeatFieldsIndex > -1) {
                await this.$refs[`componentRef${repeatFieldsIndex}`][0].validationForm()
              }

              // Creo el objeto para enviar al server + custom params
              const params = formRow?.actions?.click?.params
              const dataToSendToServer = params ? Object.assign({}, this.formObjectToSendToServer, params) : this.formObjectToSendToServer

              if (this.endpointUriWhereGetValuesToLoadInEditMode) {
                // Verifico los permisos del usuario para actualizar
                // if (!this.userPrivileges?.allowUpdate) {
                //   return this.lmToastDangerMessage('Lo sentimos, no tiene permisos para realizar esta acción.', 'Error al actualizar')
                // }
                this.$refs.lmFormRef.showLoading()
                const pathUpdate = formRow?.actions?.click?.arguments?.pathUpdate || this.apiRestForHandleAllHttpMethods
                await commonsApi.axiosPutWrapper(pathUpdate + this.$route.params.id, dataToSendToServer)
                this.lmToastSuccessMessage(this.messageAfterPOSTorPUT || 'Se guardó correctamente', 'Acción exitosa')
                if (this.pathToRedirectAfterPOSTorPUT) {
                  this.$router.push(this.pathToRedirectAfterPOSTorPUT)
                }
              } else {
                // Verifico los permisos del usuario para guardar
                // if (!this.userPrivileges?.allowCreate) {
                //   return this.lmToastDangerMessage('Lo sentimos, no tiene permisos para realizar esta acción.', 'Error al guardar')
                // }
                this.$refs.lmFormRef.showLoading()
                await commonsApi.axiosPostWrapper(formRow?.actions?.click?.arguments?.path || this.apiRestForHandleAllHttpMethods, dataToSendToServer)
                this.lmToastSuccessMessage(this.messageAfterPOSTorPUT || 'Se guardó correctamente', 'Acción exitosa')
                if (this.pathToRedirectAfterPOSTorPUT) {
                  this.$router.push(this.pathToRedirectAfterPOSTorPUT)
                }
              }
              break
            case 'cancel':
              this.$router.go(-1)
              break
            case 'delete':
              if (formRow?.actions?.click?.arguments?.deleteMethod === 'put') {
              // Borrado lógico
                const propertyToUpdate = {}
                propertyToUpdate[formRow?.actions?.click?.arguments?.logicDeleteProperty] = formRow?.actions?.click?.arguments?.logicDeleteValue
                propertyToUpdate.id = this.$route.params.id
                await commonsApi.axiosPutWrapper(formRow?.actions?.click?.arguments?.pathDelete || this.apiRestForHandleAllHttpMethods, propertyToUpdate)
                this.lmToastSuccessMessage('Eliminado correctamente.', 'Acción exitosa')
                this.$router.push(this.pathToRedirectAfterPOSTorPUT)
              } else if (formRow?.actions?.click?.arguments?.deleteMethod === 'delete') {
              // Borrado permanente
                await commonsApi.axiosDeleteWrapper(formRow?.actions?.click?.arguments?.pathDelete || this.apiRestForHandleAllHttpMethods + this.$route.params.id)
                this.lmToastSuccessMessage('Eliminado correctamente.', 'Acción exitosa')
                this.$router.push(this.pathToRedirectAfterPOSTorPUT)
              }
              break
            default:
              break
          }
          // Si tengo archivos para eliminar en el servidor
          if (this.filesToRemove.length) {
            await this.removeFilesFromServer()
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.$refs.lmFormRef.hideLoading()
      }
    },
    setFormElements () {
      this.formElements.forEach((component, index) => {
        if (component.formObjectToSendToServerProperty) {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, '')
        }
        if (component.componentName === 'LmSuperPowerSelectV2') {
          // this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, { id: '', name: '' })
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, [])
        }
        if (component.componentName === 'lmSuperPowerSelectV2Multiple' && component?.componentProps.multiple) {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, [])
        }
        if (component.componentName === 'LmLeaFletMap') {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, {})
        }
        if (
          (component.componentName === 'LmDate' || component.componentName === 'LmDateTime') &&
        this.formElements[index].componentProps &&
        this.formElements[index].componentProps.config
        ) {
          this.formElements[index].componentProps.config.locale = Spanish
        }
        if (component?.componentProps?.urlPathFromGetOptions) {
          this.formElements[index].componentProps.urlPathFromGetOptions = component?.componentProps?.urlPathFromGetOptions
        }

        // Nota: Esta línea se encarga de setear los valores por defecto si el componente tiene "componentProps.defaultValue"
        // Seteo los valores por defecto
        if (component?.componentProps?.defaultValue && !this.isOnEditMode) {
          this.$set(this.formObjectToSendToServer, component.formObjectToSendToServerProperty, component.componentProps.defaultValue)
        }
        if (component?.componentName === 'LmCheckbox' && !this.isOnEditMode) {
          if (component?.componentProps?.type === 'single') {
            if (component?.componentProps?.singleSelected) {
              this.$set(
                this.formObjectToSendToServer,
                component.formObjectToSendToServerProperty,
                component.componentProps?.checkValue || true
              )
            } else {
              this.$set(
                this.formObjectToSendToServer,
                component.formObjectToSendToServerProperty,
                component.componentProps?.uncheckedValue || false
              )
            }
          } else if (component?.componentProps?.type === 'multiple') {
            if (component?.componentProps?.options?.length) {
              this.$set(
                this.formObjectToSendToServer,
                component.formObjectToSendToServerProperty,
                component.componentProps?.options?.filter(el => el.checked).map(item => item.value)
              )
            }
          }
        }
      })
    },
    submit () {
      this.$emit('formObjectToSendToServerChange', this.formObjectToSendToServer)
    }
  },
  watch: {
    endpointUriWhereGetValuesToLoadInEditMode () {
      // Este valor puede cambiar si al invocar al formbuilder se pasa como un valor dinamico
      // LmFormBuilderPreview. es un ejemplo de este caso
      this.setForm()
    },
    endpointUriwhereGetComponents () {
      this.initializeComponent()
    },
    // Permite cargar contenido desde un template de contenido en un formbuilder hibrido
    contentTemplateData: {
      deep: true,
      handler (val) {
        // Si tengo contenido, lo cargo
        if (Object.keys(val).length && !Array.isArray(val)) {
          for (const key in val) {
            if (this.formObjectToSendToServer.hasOwnProperty(key)) {
              this.formObjectToSendToServer[key] = val[key]
            }
          }
        } else {
          // Si apreto "x" en el SP de content template
          this.setFormElements()
          this.$v.$reset()
        }
      }
    },
    'getComponents.pageTitleData' () {
      this.pageTitleData = this.getComponents.pageTitleData
    },
    'getComponents.apiRestForHandleAllHttpMethods' () {
      this.apiRestForHandleAllHttpMethods = this.getComponents.apiRestForHandleAllHttpMethods
    },
    'getComponents.validations' () {
      this.validationsData = _.cloneDeep(this.getComponents.validations)
      this.originalValidations = _.cloneDeep(this.getComponents.validations)
    },
    'getComponents.formElements': {
      handler: function () {
        this.formElements = _.cloneDeep(this.getComponents.formElements)
        this.setFormElements()
      },
      deep: true
    },
    formObjectToSendToServer: {
      deep: true,
      handler (val) {
        // Me aseguro de no enviar mal los "names" de los campos
        for (const item in val) {
          if (!this.formElements.some(el => el.formObjectToSendToServerProperty === item)) {
            delete val[item]
          }
        }

        // Horrible forma de persistir los datos del formbuilder cuando se crea un documento
        // desde un proceso
        if (this.formValues && this.firstLoad) {
          for (const key in this.formValues) {
            if (val.hasOwnProperty(key)) {
              val[key] = this.formValues[key]
            }
          }
          this.firstLoad = false
        }

        this.$emit('formObjectToSendToServerChange', val)
      }
    },
    formValues: {
      deep: true,
      handler (val) {
        if (val && Object.keys(val).length) {
          for (const key in val) {
            if (this.formObjectToSendToServer.hasOwnProperty(key)) {
              this.formObjectToSendToServer[key] = val[key]
            }
          }
        }
      }
    }
  },
  async created () {
    // NOTA. con el tema de los editores, es importante saber si el form esta en
    // modo edición o modo nuevo
    if (this.isParentOnEditMode) {
      this.isOnEditMode = true
    }

    try {
      this.isLoading = true
      this.$emit('is-loading', this.isLoading)
      await this.initializeComponent()
      this.setFormElements()
      await this.setForm()
      this.$nextTick(() => {
        this.onRegenerateValidations()
      })

      // NOTA: Esto no es generico, solo sirve para el workflow de documentos
      if (this.document?.lumbreVersion === 1) {
        this.formElements.forEach(el => {
          if (el?.componentProps?.isEditableByPickedUser) {
            el.componentProps.isEditableByPickedUser = false
          }
        })
      } else if (this.document?.lumbreVersion === 2) {
        if (!this.isOnEditMode) {
          const user = this.$store.getters['session/userInfo']
          let hideCollaborators = true
          this.formElements.forEach((el) => {
            if (el?.componentProps?.isEditableByPickedUser) {
              hideCollaborators = false
              this.$emit('editor-change', {
                editor: {
                  id: user.id,
                  name: `${user.firstname} ${user.lastname}`
                },
                element: el
              })
            }
            if (el.componentName === 'LmRepeatFields') {
              el.componentProps.initialItems.formElements.forEach((iel) => {
                if (iel.componentProps?.isEditableByPickedUser) {
                  hideCollaborators = false
                  this.$emit('editor-change', {
                    editor: {
                      id: user.id,
                      name: `${user.firstname} ${user.lastname}`
                    },
                    element: el,
                    subelement: `0.${iel.formObjectToSendToServerProperty}`
                  })
                }
              })
            }
          })
          if (hideCollaborators) {
            this.$emit('hide-collaborators-workflow')
          }
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
      this.$emit('is-loading', this.isLoading)
    }
  }
}
</script>

<style lang="sass" scoped>
::v-deep
  .form-wrapper
    margin-right: -15px
    margin-left: -15px
  #form
    display: -ms-flexbox
    display: -webkit-box
    display: flex
    -ms-flex-wrap: wrap
    flex-wrap: wrap
    width: 100%
    .invalid-feedback
      position: absolute
</style>
